/**
 * PhotoGallery — dedicated photos page for a testament license.
 *
 * Upload, view, reorder, caption, delete, and AI-restore/enhance photos
 * stored in Cloudinary with metadata in the `tribute_photos` table.
 *
 * Can be rendered standalone (route: /photos/:testamentLicenseId)
 * or embedded via <PhotoGalleryPanel /> in the CyranoEditor sidebar.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";

// ── Cyrano palette ──
// Accent slots keep the `gold*` names for minimal diff — values are on-palette
// pink-lavender (#d4adbb, the "comfort and warmth" colour from the brand guide).
const B = {
  navy: "#1C2951", navyMid: "#2a3d6b",
  gold: "#d4adbb", goldPale: "#f0e4ea", goldFaint: "#faf5f8",
  cream: "#FAF8F5", creamDark: "#F2EDE7",
  lavender: "#d4adbb", lavenderPale: "#f0e4ea",
  wg: "#6B6B6B", wgL: "#9a9a9a", wgP: "#e8e5e0", wgF: "#f3f1ed",
  tx: "#2c2c2c", txM: "#555", txL: "#888",
  w: "#fff",
  ok: "#2d8a56", okBg: "#edf7f0",
  err: "#b5392a", errBg: "#fdf0ef",
};

interface Photo {
  id: string;
  public_url: string;
  thumbnail_url: string;
  file_name: string;
  caption: string | null;
  sort_order: number;
  status: string;
  width_pixels: number;
  height_pixels: number;
  orientation: string;
  storage_path: string;
  is_featured: boolean;
  uploaded_by_name: string | null;
  uploaded_at: string;
}

// ════════════════════════════════════════════════════════════════════
// PhotoGalleryPanel — embeddable panel (for CyranoEditor sidebar tab)
// ════════════════════════════════════════════════════════════════════
export function PhotoGalleryPanel({ testamentLicenseId }: { testamentLicenseId: string }) {
  return (
    <PhotoGalleryInner
      testamentLicenseId={testamentLicenseId}
      compact
    />
  );
}

// ════════════════════════════════════════════════════════════════════
// PhotoGallery — full page (route: /photos/:testamentLicenseId)
// ════════════════════════════════════════════════════════════════════
export default function PhotoGallery() {
  const { testamentLicenseId } = useParams<{ testamentLicenseId: string }>();
  const navigate = useNavigate();

  if (!testamentLicenseId) {
    return (
      <div style={{ minHeight: "100vh", background: B.cream, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: B.txM }}>No testament license ID provided.</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: B.cream }}>
      {/* Header */}
      <div style={{ background: B.w, borderBottom: `1px solid ${B.wgP}`, padding: "16px 24px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 18, color: B.navy, padding: 4 }}>{"\u2190"}</button>
        <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/image48.png" alt="" style={{ width: 24, height: 24, opacity: 0.7 }} />
        <h1 style={{ margin: 0, fontSize: 20, fontFamily: "'Playfair Display',Georgia,serif", color: B.navy, fontWeight: 600 }}>Photos</h1>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 16px" }}>
        <PhotoGalleryInner testamentLicenseId={testamentLicenseId} compact={false} />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Core inner component
// ════════════════════════════════════════════════════════════════════
function PhotoGalleryInner({ testamentLicenseId, compact }: { testamentLicenseId: string; compact: boolean }) {
  const { getToken } = useAuth();

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [editCaption, setEditCaption] = useState("");
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [restoredUrls, setRestoredUrls] = useState<Record<string, { restored?: string; enhanced?: string }>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // ── Load photos ──
  const loadPhotos = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;

      const resp = await fetch(`/api/photos/list?testamentLicenseId=${testamentLicenseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Failed to load photos");
      const data = await resp.json();
      setPhotos(data.photos || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [testamentLicenseId, getToken]);

  useEffect(() => { loadPhotos(); }, [loadPhotos]);

  // ── Upload handler ──
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError(null);

    const token = await getToken();
    if (!token) { setUploading(false); return; }

    let uploaded = 0;
    for (const file of Array.from(files)) {
      // Validate type — allow empty type for HEIC on some iOS versions
      const ext = file.name.split(".").pop()?.toLowerCase() || "";
      const imageExts = ["jpg", "jpeg", "png", "webp", "heic", "heif"];
      if (!file.type.startsWith("image/") && !imageExts.includes(ext)) {
        setError(`${file.name} is not an image`);
        continue;
      }
      // Validate size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError(`${file.name} exceeds 10MB limit`);
        continue;
      }

      setUploadProgress(`Uploading ${file.name}... (${uploaded + 1}/${files.length})`);

      try {
        // Convert to base64
        const base64 = await fileToBase64(file);

        const resp = await fetch("/api/photos/upload", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fileData: base64,
            testamentLicenseId,
            fileName: file.name,
          }),
        });

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.error || "Upload failed");
        }
        uploaded++;
      } catch (err: any) {
        setError(`Failed to upload ${file.name}: ${err.message}`);
      }
    }

    setUploadProgress("");
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    await loadPhotos();
  };

  // ── Delete handler ──
  const handleDelete = async (photoId: string) => {
    if (!confirm("Delete this photo? This cannot be undone.")) return;
    setDeletingId(photoId);

    try {
      const token = await getToken();
      const resp = await fetch("/api/photos/delete", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ photoId }),
      });
      if (!resp.ok) throw new Error("Delete failed");
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      if (selectedPhoto?.id === photoId) setSelectedPhoto(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // ── AI Restore/Enhance handler ──
  const handleRestore = async (photoId: string, mode: "restore" | "enhance" | "both") => {
    setRestoringId(photoId);
    try {
      const token = await getToken();
      const resp = await fetch("/api/photos/restore", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ photoId, mode }),
      });
      if (!resp.ok) throw new Error("Restore failed");
      const data = await resp.json();
      setRestoredUrls(prev => ({ ...prev, [photoId]: { ...prev[photoId], ...data.urls } }));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRestoringId(null);
    }
  };

  // ── Render ──
  if (loading) {
    return (
      <div style={{ padding: compact ? 0 : 40, textAlign: "center" }}>
        <div style={{ width: 24, height: 24, border: `2px solid ${B.wgP}`, borderTopColor: B.navy, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
        <p style={{ fontSize: 14, color: B.wgL, marginTop: 12 }}>Loading photos...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Error banner */}
      {error && (
        <div style={{ background: B.errBg, border: `1px solid ${B.err}30`, borderRadius: 8, padding: "10px 14px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, color: B.err }}>{error}</span>
          <button onClick={() => setError(null)} style={{ border: "none", background: "none", cursor: "pointer", color: B.err, fontSize: 18, minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" }}>{"\u2715"}</button>
        </div>
      )}

      {/* Upload area */}
      <div
        onClick={() => !uploading && fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${B.wgP}`,
          borderRadius: 12,
          padding: compact ? "16px 12px" : "24px 20px",
          textAlign: "center",
          cursor: uploading ? "wait" : "pointer",
          background: B.w,
          marginBottom: 20,
          transition: "border-color 0.2s, background 0.2s",
        }}
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = B.gold; e.currentTarget.style.background = B.goldFaint; }}
        onDragLeave={(e) => { e.currentTarget.style.borderColor = B.wgP; e.currentTarget.style.background = B.w; }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = B.wgP;
          e.currentTarget.style.background = B.w;
          if (e.dataTransfer.files.length > 0 && fileInputRef.current) {
            // Trigger via the input for the handler
            const dt = new DataTransfer();
            for (const f of Array.from(e.dataTransfer.files)) dt.items.add(f);
            fileInputRef.current.files = dt.files;
            fileInputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
          multiple
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />
        {/* Hidden camera input — capture="environment" opens rear camera */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/jpeg,image/png,image/heic,image/heif"
          capture="environment"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />
        {uploading ? (
          <div>
            <div style={{ width: 24, height: 24, border: `2px solid ${B.wgP}`, borderTopColor: B.navy, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 8px" }} />
            <p style={{ fontSize: 14, color: B.txM, margin: 0 }}>{uploadProgress}</p>
          </div>
        ) : (
          <>
            <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/image48.png" alt="" style={{ width: compact ? 28 : 36, height: compact ? 28 : 36, opacity: 0.5, marginBottom: 8 }} />
            <p style={{ fontSize: 14, color: B.txM, margin: 0, fontWeight: 500 }}>
              {compact ? "Tap to add photos" : "Click or drag photos here"}
            </p>
            <p style={{ fontSize: 14, color: B.wgL, margin: "4px 0 0" }}>JPG, PNG, WebP, HEIC — up to 10MB each</p>
          </>
        )}
      </div>

      {/* Take a photo — opens camera directly (mobile) */}
      {!uploading && (
        <button
          onClick={() => cameraInputRef.current?.click()}
          style={{
            width: "100%", padding: "12px 16px", marginBottom: 20,
            border: `1px solid ${B.navy}22`, borderRadius: 10,
            background: B.w, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            minHeight: 44,
          }}
        >
          <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/camera48.png" alt="" style={{ width: 22, height: 22, opacity: 0.6 }} />
          <span style={{ fontSize: 14, fontWeight: 500, color: B.navy, fontFamily: "'Inter',system-ui,sans-serif" }}>
            Take a photo of a printed photo
          </span>
        </button>
      )}

      {/* Photo count */}
      {photos.length > 0 && (
        <p style={{ fontSize: 14, color: B.wg, marginBottom: 12, fontWeight: 500 }}>
          {photos.length} photo{photos.length !== 1 ? "s" : ""}
        </p>
      )}

      {/* Photo grid */}
      {photos.length === 0 && !uploading ? (
        <div style={{ textAlign: "center", padding: compact ? "20px 0" : "40px 0" }}>
          <p style={{ fontSize: 14, color: B.wgL, lineHeight: 1.6 }}>No photos yet. Upload photos to include in the tribute.</p>
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: compact ? "repeat(2, 1fr)" : "repeat(auto-fill, minmax(160px, 1fr))",
          gap: compact ? 8 : 12,
        }}>
          {photos.map(photo => (
            <div
              key={photo.id}
              onClick={() => { setSelectedPhoto(photo); setEditCaption(photo.caption || ""); }}
              style={{
                position: "relative",
                borderRadius: 10,
                overflow: "hidden",
                cursor: "pointer",
                border: selectedPhoto?.id === photo.id ? `2px solid ${B.gold}` : `1px solid ${B.wgP}`,
                background: B.w,
                transition: "border-color 0.2s, box-shadow 0.2s",
                boxShadow: selectedPhoto?.id === photo.id ? `0 0 0 3px ${B.gold}30` : "none",
              }}
            >
              <div style={{ paddingTop: "100%", position: "relative" }}>
                <img
                  src={photo.thumbnail_url || photo.public_url}
                  alt={photo.caption || photo.file_name}
                  loading="lazy"
                  style={{
                    position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
              {photo.is_featured && (
                <div style={{
                  position: "absolute", top: 6, left: 6,
                  background: B.gold, color: B.w, fontSize: 9, fontWeight: 700,
                  padding: "2px 6px", borderRadius: 4, textTransform: "uppercase", letterSpacing: 0.5,
                }}>Featured</div>
              )}
              {photo.status === "enhanced" && (
                <div style={{
                  position: "absolute", top: 6, right: 6,
                  background: B.ok, color: B.w, fontSize: 9, fontWeight: 700,
                  padding: "2px 6px", borderRadius: 4, textTransform: "uppercase",
                }}>Enhanced</div>
              )}
              {photo.caption && (
                <div style={{
                  position: "absolute", bottom: 0, left: 0, right: 0,
                  background: "linear-gradient(transparent, rgba(0,0,0,0.6))",
                  padding: "16px 8px 6px", fontSize: 11, color: B.w,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>{photo.caption}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Selected photo detail panel */}
      {selectedPhoto && (
        <div style={{
          marginTop: 20, background: B.w, borderRadius: 12,
          border: `1px solid ${B.wgP}`, overflow: "hidden",
        }}>
          {/* Full image */}
          <div style={{ position: "relative", background: B.creamDark }}>
            <img
              src={restoredUrls[selectedPhoto.id]?.restored || restoredUrls[selectedPhoto.id]?.enhanced || selectedPhoto.public_url}
              alt={selectedPhoto.caption || selectedPhoto.file_name}
              style={{ width: "100%", maxHeight: compact ? 250 : 400, objectFit: "contain", display: "block" }}
            />
            <button
              onClick={() => setSelectedPhoto(null)}
              style={{
                position: "absolute", top: 8, right: 8, width: 44, height: 44,
                borderRadius: "50%", background: "rgba(0,0,0,0.5)", color: B.w,
                border: "none", cursor: "pointer", fontSize: 18, display: "flex",
                alignItems: "center", justifyContent: "center",
              }}
            >{"\u2715"}</button>
          </div>

          <div style={{ padding: compact ? 12 : 16 }}>
            {/* Caption */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, color: B.wg, marginBottom: 6, display: "block" }}>Caption</label>
              <input
                type="text"
                value={editCaption}
                onChange={(e) => setEditCaption(e.target.value)}
                placeholder="Add a caption..."
                style={{
                  width: "100%", padding: "10px 12px", border: `1px solid ${B.wgP}`,
                  borderRadius: 6, fontSize: 16, color: B.tx, fontFamily: "'Georgia',serif",
                  boxSizing: "border-box",
                }}
                onBlur={() => {
                  if (editCaption === (selectedPhoto.caption || "")) return;
                  // Update locally — caption persists in UI state; a dedicated save endpoint could be added later
                  setPhotos(prev => prev.map(p => p.id === selectedPhoto.id ? { ...p, caption: editCaption } : p));
                  setSelectedPhoto({ ...selectedPhoto, caption: editCaption });
                }}
              />
            </div>

            {/* Info */}
            <div style={{ fontSize: 14, color: B.wgL, marginBottom: 12, lineHeight: 1.6 }}>
              <span>{selectedPhoto.width_pixels}×{selectedPhoto.height_pixels}</span>
              <span style={{ margin: "0 8px" }}>{"\u00B7"}</span>
              <span>{selectedPhoto.orientation}</span>
              {selectedPhoto.uploaded_by_name && (
                <>
                  <span style={{ margin: "0 8px" }}>{"\u00B7"}</span>
                  <span>by {selectedPhoto.uploaded_by_name}</span>
                </>
              )}
            </div>

            {/* AI restore / enhance buttons */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <button
                onClick={() => handleRestore(selectedPhoto.id, "restore")}
                disabled={restoringId === selectedPhoto.id}
                style={{
                  flex: 1, minWidth: 120, padding: "10px 12px", minHeight: 44,
                  border: `1px solid ${B.lavender}60`, borderRadius: 8,
                  background: B.lavenderPale, color: B.navy, fontSize: 14, fontWeight: 500,
                  cursor: restoringId === selectedPhoto.id ? "wait" : "pointer",
                  fontFamily: "'Inter',system-ui,sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/reload_cw48.png" alt="" style={{ width: 14, height: 14, opacity: 0.6 }} />
                {restoringId === selectedPhoto.id ? "Processing..." : "AI Restore"}
              </button>
              <button
                onClick={() => handleRestore(selectedPhoto.id, "enhance")}
                disabled={restoringId === selectedPhoto.id}
                style={{
                  flex: 1, minWidth: 120, padding: "10px 12px", minHeight: 44,
                  border: `1px solid ${B.gold}60`, borderRadius: 8,
                  background: B.goldFaint, color: B.navy, fontSize: 14, fontWeight: 500,
                  cursor: restoringId === selectedPhoto.id ? "wait" : "pointer",
                  fontFamily: "'Inter',system-ui,sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/star48.png" alt="" style={{ width: 14, height: 14, opacity: 0.6 }} />
                {restoringId === selectedPhoto.id ? "Processing..." : "Enhance"}
              </button>
            </div>

            {/* Restored/enhanced — use this version */}
            {restoredUrls[selectedPhoto.id] && (
              <div style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 14, color: B.txM, marginBottom: 8 }}>
                  AI processing complete. Preview above shows the improved version. Use it as the permanent photo for exports:
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {restoredUrls[selectedPhoto.id].restored && (
                    <button
                      onClick={() => {
                        const url = restoredUrls[selectedPhoto.id].restored!;
                        // The restore endpoint already persisted the URL to DB.
                        // Update local state so the gallery shows the restored version.
                        setPhotos(prev => prev.map(p => p.id === selectedPhoto.id ? { ...p, public_url: url, status: "enhanced" } : p));
                        setSelectedPhoto({ ...selectedPhoto, public_url: url, status: "enhanced" });
                        setRestoredUrls(prev => { const copy = { ...prev }; delete copy[selectedPhoto.id]; return copy; });
                      }}
                      style={{
                        flex: 1, minHeight: 44, padding: "10px 12px",
                        border: `1px solid ${B.navy}40`, borderRadius: 8,
                        background: B.navy, color: B.w, fontSize: 14, fontWeight: 600,
                        cursor: "pointer", fontFamily: "'Inter',system-ui,sans-serif",
                      }}
                    >Use restored version</button>
                  )}
                  {restoredUrls[selectedPhoto.id].enhanced && (
                    <button
                      onClick={() => {
                        const url = restoredUrls[selectedPhoto.id].enhanced!;
                        // The restore endpoint already persisted the URL to DB.
                        setPhotos(prev => prev.map(p => p.id === selectedPhoto.id ? { ...p, public_url: url, status: "enhanced" } : p));
                        setSelectedPhoto({ ...selectedPhoto, public_url: url, status: "enhanced" });
                        setRestoredUrls(prev => { const copy = { ...prev }; delete copy[selectedPhoto.id]; return copy; });
                      }}
                      style={{
                        flex: 1, minHeight: 44, padding: "10px 12px",
                        border: `1px solid ${B.gold}60`, borderRadius: 8,
                        background: B.gold, color: B.w, fontSize: 14, fontWeight: 600,
                        cursor: "pointer", fontFamily: "'Inter',system-ui,sans-serif",
                      }}
                    >Use enhanced version</button>
                  )}
                </div>
              </div>
            )}

            {/* Delete button */}
            <button
              onClick={() => handleDelete(selectedPhoto.id)}
              disabled={deletingId === selectedPhoto.id}
              style={{
                width: "100%", padding: "10px 12px", minHeight: 44,
                border: `1px solid ${B.err}30`, borderRadius: 8,
                background: B.w, color: B.err, fontSize: 14, fontWeight: 500,
                cursor: deletingId === selectedPhoto.id ? "wait" : "pointer",
                fontFamily: "'Inter',system-ui,sans-serif",
              }}
            >
              {deletingId === selectedPhoto.id ? "Deleting..." : "Delete photo"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helper: file → base64 data URI ──
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
